import React, { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  styled,
  useTheme,
  type SxProps,
  type Theme,
} from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, Box, IconButton } from "@mui/material";
import Button from "../button";
import Typography from "../typography";
import FieldError from "../fieldError";
import { convertHeicToJpeg, isHeic } from "utils/heic";

interface UploadFileProps {
  disabled?: boolean;
  onChange: (file: File | null) => void;
  onDelete?: () => void;
  label?: string;
  loading?: boolean;
  file?: File | null;
  error?: string;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
  /**
   * Fired once the preview has rendered at its real size. Callers that need to
   * react to the height it adds must wait for this rather than `onChange`,
   * which runs while the preview is still zero-height.
   */
  onPreviewLoad?: () => void;
}

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const FileListContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

const FileItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  fontSize: "0.875rem",
}));

const UploadFile: React.FC<UploadFileProps> = ({
  onChange,
  onDelete,
  label,
  disabled = false,
  loading = false,
  file,
  error,
  fullWidth = false,
  sx,
  onPreviewLoad,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);

  const busy = loading || converting;

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const unconvertedHeic = !!file && isHeic(file);
  const isImage = file?.type.startsWith("image/") && !unconvertedHeic;
  const isPdf = file?.type === "application/pdf";

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (!selected) {
      return;
    }

    if (!isHeic(selected)) {
      onChange(selected);
      return;
    }

    setConverting(true);
    try {
      onChange(await convertHeicToJpeg(selected));
    } finally {
      setConverting(false);
    }
  };

  const handleInputClick = () => {
    // Clear the input value when user clicks to open file picker
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleDelete = () => {
    onChange(null);
    onDelete?.();
  };

  return (
    <Box
      sx={{
        width: fullWidth ? "100%" : "auto",
        justifyContent: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: fullWidth ? "stretch" : "center",
        ...sx,
      }}
    >
      <Button
        variant="contained"
        endAdornment={
          busy ? (
            <CircularProgress size={20} sx={{ color: "white" }} />
          ) : (
            <CloudUploadIcon />
          )
        }
        disabled={disabled || busy}
        component="label"
        fullWidth={fullWidth}
      >
        {converting
          ? t("ui.converting")
          : loading
            ? t("ui.uploading")
            : (label ?? t("ui.upload_file"))}
        <VisuallyHiddenInput
          ref={inputRef}
          type="file"
          onChange={handleChange}
          onClick={handleInputClick}
          disabled={disabled || busy}
        />
      </Button>

      {file && (
        <FileListContainer>
          <FileItem>
            {!busy ? (
              <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />
            ) : (
              <InsertDriveFileIcon
                sx={{ fontSize: 16, color: "action.active" }}
              />
            )}
            <Typography
              variant="body2"
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {file.name}
            </Typography>
            <Typography variant="caption" style={{ color: "text.secondary" }}>
              {(file.size / 1024).toFixed(1)} KB
            </Typography>
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{
                padding: 0.5,
                "&:hover": { color: "error.main" },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </FileItem>

          {unconvertedHeic && (
            <Typography
              variant="caption"
              style={{ color: theme.palette.text.secondary }}
            >
              {t("ui.preview_unavailable")}
            </Typography>
          )}

          {previewUrl && isImage && (
            <Box
              sx={{
                mt: 1,
                borderRadius: 1,
                overflow: "hidden",
                border: `1px solid ${theme.palette.grey[300]}`,
                maxWidth: 320,
                alignSelf: "center",
              }}
            >
              <img
                src={previewUrl}
                alt="preview"
                onLoad={onPreviewLoad}
                style={{
                  width: "100%",
                  display: "block",
                  maxHeight: 240,
                  objectFit: "contain",
                }}
              />
            </Box>
          )}

          {previewUrl && isPdf && (
            <Box
              sx={{
                mt: 1,
                borderRadius: 1,
                overflow: "hidden",
                border: `1px solid ${theme.palette.grey[300]}`,
                width: "100%",
                maxWidth: 320,
                height: 320,
                alignSelf: "center",
              }}
            >
              <iframe
                src={previewUrl}
                title={t("ui.pdf_preview")}
                onLoad={onPreviewLoad}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            </Box>
          )}
        </FileListContainer>
      )}

      <FieldError error={error} />
    </Box>
  );
};

export default UploadFile;
